import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import AuthInputComponent from '../../components/AuthInputComponent';
import {
  AdminPageUrl,
  DiagnosisPageUrl,
  FindIdPageUrl,
  FindPasswordPageUrl,
  SignupPageUrl,
  StuffPageUrl,
} from '../../url/poenUrl';
import AuthFooter from '../../components/auth/AuthFooter';
import AuthBackgroundLogo from '../../components/auth/AuthBackgroundLogo';
import { useNavigate } from 'react-router';
import AuthService from '../../api/service/auth/AuthService';
import PasswordInputComponent from '../../components/PasswordInputComponent';
import PermissionRedirect from '../../components/PermissionRedirect';

// Styled-components로 스타일 정의
const LoginContainer = styled.div`
  padding: 20px;
  font-family: 'Pretendard', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #fff;
  position: relative;
  gap: 70px;
`;

const Title = styled.h1`
  font-size: 30px;
  font-weight: bold;
`;

const LoginForm = styled.div`
  z-index: 1;
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 300px;
  gap: 10px;
`;

const Line = styled.div`
  background-color: ${({ theme }) => theme.colors.inputLine};
  margin: 30px 0px;
  height: 1px;

  @media (max-width: 768px) {
    margin: 20px 0px;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  color: white;
  cursor: pointer;

  background-color: ${({ theme }) => theme.colors.mainColor};

  &:hover {
    background-color: ${({ theme }) => theme.colors.hoverColor};
  }
`;

const SinupButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.subColor};

  &:hover {
    background-color: ${({ theme }) => theme.colors.subHoverColor};
  }
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');

  // 페이지 접근 제한
  useEffect(() => {
    const permissions = localStorage.getItem('permissions');
    const accessToken = localStorage.getItem('accessToken');

    if (permissions && accessToken) {
      switch (permissions) {
        case 'ADMIN':
          navigate(AdminPageUrl); // ADMIN 사용자 -> AdminPage로 이동
          break;
        case 'SELLER':
          navigate(DiagnosisPageUrl); // SELLER 사용자 -> SellerPage로 이동
          break;
        default:
          navigate(StuffPageUrl); // 기타 사용자 -> StuffPage로 이동
          break;
      }
    }
  }, [navigate]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      try {
        const { token, name, permissions } = JSON.parse(event.data);

        if (!token || !permissions) return;

        localStorage.setItem('accessToken', token);
        localStorage.setItem('name', name);
        localStorage.setItem('permissions', permissions);

        switch (permissions) {
          case 'ADMIN':
            navigate(AdminPageUrl);
            break;
          case 'SELLER':
            navigate(DiagnosisPageUrl);
            break;
          default:
            navigate(StuffPageUrl);
            break;
        }
      } catch (err) {
        console.error('앱 메시지 처리 실패:', err);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!identifier || !password) {
      alert('아이디와 비밀번호를 입력하세요.'); // 알림 추가 (선택)
      return;
    }

    try {
      const response = await AuthService.login({ identifier, password }); // AuthService 호출
      alert(response.resultMsg); // 성공 알림 (선택)
      console.log('로그인 성공:', response);

      // 사용자 권한 배열 저장
      const permissions = response.data.permissions; // 사용자 권한 배열
      if (permissions && permissions.length > 0) {
        localStorage.setItem('permissions', permissions[0]); // 첫 번째 배열 값 저장
      }
      // 사용자 이름과 식별자 저장
      const { name, identifier: userIdentifier } = response.data; // name과 identifier 추출
      if (name) {
        localStorage.setItem('name', name); // 사용자 이름 저장
      }
      if (userIdentifier) {
        localStorage.setItem('identifier', userIdentifier); // 사용자 식별자 저장
      }

      if (permissions[0] === 'SELLER') {
        navigate(DiagnosisPageUrl); // SELLER 권한인 경우 SellerPage로 이동
      } else {
        navigate(StuffPageUrl); // 기본 StuffPage로 이동
      }
    } catch (error) {
      alert(error); // 실패 알림 (선택)
      console.error('로그인 에러:', error);
    }
  };

  return (
    <LoginContainer>
      <PermissionRedirect />
      <Title>LOGIN</Title>
      <LoginForm>
        <Form onSubmit={handleSubmit}>
          <AuthInputComponent
            type="text"
            placeholder="아이디"
            value={identifier}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setIdentifier(e.target.value)
            }
          />
          <PasswordInputComponent
            placeholder="비밀번호"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)
            }
          />
          <Line />
          <Button type="submit">로그인</Button>
          <SinupButton type="button" onClick={() => navigate(SignupPageUrl)}>
            회원가입
          </SinupButton>
        </Form>
        <AuthFooter
          firstUrl={FindIdPageUrl}
          secondUrl={FindPasswordPageUrl}
          findIdText="아이디 찾기"
          findPasswordText="비밀번호 찾기"
        />
      </LoginForm>
      <AuthBackgroundLogo />
    </LoginContainer>
  );
};

export default LoginPage;
